import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  Tooltip,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { getDebts } from "../redux/actions/debtActions";
import { DebtSummaryDto } from "../shared/dtos/debt/DebtSummaryDto";
import { formatPlainNumber } from "../utils/appUtils";

const DebtsPage: React.FC = React.memo(() => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const {
    debts,
    pagination,
    loadingFetchAll: loadingDebts,
    errorFetchAll: errorLoadingDebts,
  } = useAppSelector((state) => state.debt);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [includeCleared, setIncludeCleared] = useState<boolean>(false);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const canRead = hasPermission("debt:read_all");

  const fetchDebtsData = useCallback((): void => {
    if (!canRead) return;
    dispatch(
      getDebts({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        includeCleared,
      }),
    );
  }, [dispatch, pageConfig, debouncedSearch, includeCleared, canRead]);

  useEffect(() => {
    fetchDebtsData();
  }, [fetchDebtsData]);
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, includeCleared]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, includeCleared]);

  const columns: TableColumn<DebtSummaryDto>[] = useMemo(
    () => [
      // --- DEFAULT VISIBLE COLUMNS ---
      {
        id: "customerName",
        label: "Customer",
        minWidth: 150,
        format: (_, row) => <b>{row.customerName}</b>,
      },
      {
        id: "receiptNumber",
        label: "Receipt #",
        minWidth: 120,
        format: (_, row) => (
          <Typography variant="body2">{row.receiptNumber}</Typography>
        ),
      },
      {
        id: "date",
        label: "Date",
        minWidth: 120,
        format: (_, row) => new Date(row.date).toLocaleDateString(),
      },
      {
        id: "currencyCode",
        label: "CUR",
        minWidth: 60,
        align: "center",
        format: (_, row) => row.currencyCode || "KES",
      },
      {
        id: "total",
        label: "Total Debt",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.total)}
          </Typography>
        ),
      },
      {
        id: "totalPayments",
        label: "Paid",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            sx={{ color: "success.main", fontWeight: "bold" }}
          >
            {formatPlainNumber(row.totalPayments)}
          </Typography>
        ),
      },
      {
        id: "pendingAmount",
        label: "Pending",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            sx={{ color: "error.main", fontWeight: "bold" }}
          >
            {formatPlainNumber(row.pendingAmount)}
          </Typography>
        ),
      },
      {
        id: "cleared",
        label: "Status",
        minWidth: 100,
        format: (_, row) => (
          <StatusChip status={row.cleared ? "CLEARED" : "PENDING"} />
        ),
      },

      // --- HIDDEN BY DEFAULT ---
      {
        id: "exchangeRate",
        label: "Rate",
        minWidth: 80,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.exchangeRate),
      },
    ],
    [],
  );

  const renderRowActions = (row: DebtSummaryDto): React.JSX.Element => (
    <Tooltip title="View Debt Details">
      <IconButton
        size="small"
        color="primary"
        onClick={() => navigateToTab(`/debts/${row.id}`)}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <PageContainer
      title="Customer Debts (Accounts Receivable)"
      subtitle="Track unpaid credit sales and manage customer payments."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Debts", to: undefined },
      ]}
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search Customer or Receipt #..."
      >
        <FormControlLabel
          control={
            <Switch
              checked={includeCleared}
              onChange={(e) => setIncludeCleared(e.target.checked)}
              color="secondary"
            />
          }
          label="Show Cleared Debts"
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={debts}
        loading={loadingDebts}
        error={errorLoadingDebts}
        onRetry={fetchDebtsData}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        enableColumnManagement={true}
        rowAction={renderRowActions}
      />
    </PageContainer>
  );
});

DebtsPage.displayName = "DebtsPage";
export default DebtsPage;
